import { PARAM_LABELS } from '../constants/parameters.constants';
import type {
	ReferenceKey,
	StateReferenceDTO,
	StateReferenceFixedClass,
	StateReferenceFixedRange,
} from '../types/parameters.types';

export const isFiniteNumber = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value);

const paramLabel = (code: string) => PARAM_LABELS[code] ?? code;

export const validateClassLimits = (classes: StateReferenceFixedClass[]): string | null => {
	if (classes.length < 2) return 'Cadastre ao menos duas classes.';
	for (let i = 0; i < classes.length; i += 1) {
		const { minValue, maxValue } = classes[i];
		if (i > 0 && !isFiniteNumber(minValue)) return 'Preencha todos os limites das classes.';
		if (i < classes.length - 1 && !isFiniteNumber(maxValue)) return 'Preencha todos os limites das classes.';
		if (isFiniteNumber(minValue) && isFiniteNumber(maxValue) && minValue >= maxValue) {
			return 'Em cada classe, o limite inferior deve ser menor que o superior.';
		}
		if (i > 0 && minValue !== classes[i - 1].maxValue) {
			return 'As classes devem se conectar sem lacunas ou sobreposição.';
		}
	}
	return null;
};

export const validateFoliarValues = (values: (number | undefined)[]): string | null => {
	if (values.some((value) => !isFiniteNumber(value))) return 'Preencha todos os limites.';
	const numbers = values as number[];
	if (numbers.some((value, i) => i > 0 && value <= numbers[i - 1])) {
		return 'Os quatro limites de cada nutriente devem estar em ordem crescente.';
	}
	return null;
};

export const validateDrisValues = (values: (number | undefined)[]): string | null => {
	if (values.length !== 2 || values.some((value) => !isFiniteNumber(value))) {
		return 'Preencha a média e o desvio padrão de todas as relações.';
	}
	if ((values[1] as number) <= 0) return 'O desvio padrão deve ser maior que zero em todas as relações.';
	return null;
};

const validateRanges = (
	ranges: StateReferenceFixedRange[],
	validate: (values: (number | undefined)[]) => string | null
) => {
	for (const range of ranges) {
		const error = validate(range.range);
		if (error) return `${range.code}: ${error}`;
	}
	return null;
};

const validateRangeProfiles = (
	profiles: StateReferenceDTO['criticalRanges'],
	validate: (values: (number | undefined)[]) => string | null
) => {
	for (const profile of profiles) {
		const groups = [
			profile.fixedRanges ?? [],
			...(profile.dependentRegions ?? []).map((item) => item.fixedRanges),
			...(profile.dependentSeasons ?? []).map((item) => item.fixedRanges),
		];
		for (const group of groups) {
			const error = validateRanges(group, validate);
			if (error) return error;
		}
	}
	return null;
};

const validateIdentity = (param: { code: string; name: string; unit: string; method: string }) => {
	if (!param.name?.trim() || !param.unit?.trim() || !param.method?.trim()) {
		return `Preencha nome, unidade e método de ${paramLabel(param.code)}.`;
	}
	return null;
};

const validateFixedParam = (param: StateReferenceDTO['fixedParams'][number]) => {
	const identityError = validateIdentity(param);
	if (identityError) return identityError;
	if (!param.idealRange?.trim()) return `Preencha a faixa ideal de ${paramLabel(param.code)}.`;
	const limitsError = validateClassLimits(param.fixedClasses);
	return limitsError ? `${paramLabel(param.code)}: ${limitsError}` : null;
};

const validateDependentParam = (param: StateReferenceDTO['dependentParams'][number]) => {
	const identityError = validateIdentity(param);
	if (identityError) return identityError;
	for (const rule of param.rules) {
		for (const cls of rule.dependentClasses) {
			const limitsError = validateClassLimits(cls.fixedClasses);
			if (limitsError) return `${paramLabel(param.code)}: ${limitsError}`;
		}
	}
	return null;
};

const validateDoses = (draft: StateReferenceDTO): string | null => {
	const invalid = draft.demandP2O5.some((item) =>
		item.fixedClasses.some((cls) => !isFiniteNumber(cls.maxValue) || cls.maxValue < 0)
	);
	return invalid ? 'Preencha todas as doses de P₂O₅ com valores a partir de zero.' : null;
};

const validateParameterValues = (parameters: { code: string; value: number }[]): string | null => {
	const invalid = parameters.find((item) => !isFiniteNumber(item.value));
	return invalid ? `Preencha o valor de ${paramLabel(invalid.code)}.` : null;
};

export const validateStateReference = (draft: StateReferenceDTO, keys: ReferenceKey[]): string | null => {
	for (const key of keys) {
		let error: string | null = null;
		if (key === 'fixedParams') error = draft.fixedParams.map(validateFixedParam).find(Boolean) ?? null;
		if (key === 'dependentParams') error = draft.dependentParams.map(validateDependentParam).find(Boolean) ?? null;
		if (key === 'liming') {
			error = draft.liming.map((item) => validateParameterValues(item.parameters)).find(Boolean) ?? null;
		}
		if (key === 'plastering') error = validateParameterValues(draft.plastering);
		if (key === 'demandP2O5') error = validateDoses(draft);
		if (key === 'criticalRanges') error = validateRangeProfiles(draft.criticalRanges, validateFoliarValues);
		if (key === 'drisParams') error = validateRangeProfiles(draft.drisParams, validateDrisValues);
		if (error) return error;
	}
	return null;
};
